<template>
  <div v-if="data?.length" class="map-data-carousel-control" :class="[ {'nav-absolute': navAbsolute}, orientation ]">
    <div v-if="hasScrollbar" class="map-data-carousel-nav-arrow left shadow-2" @click="navigateTo('left')"><i class="fa fa-chevron-left"/></div>
    <div class="map-data-carousel-slides" ref="slidesWrapper" @scroll="debounce(scroll, 100)">
      <div v-for="(slide, i) in data" class="carousel-card shadow-1" :ref="`slide-${slide[slideKey]}`" @click="onSlideClick(slide)">
        <img v-if="slide.image" :src="slide.image" alt=""/>
        <div class="carousel-card-text">
          <strong v-if="slide.title">{{ slide.title }}</strong>
          <div v-if="slide.details" v-html="slide.details"></div>
          <div class="flex align-items-center">
            <Button @click.prevent.stop="onSlideAction(slide, slide.action)" size="small" severity="secondary" class="mr-1" icon="fa fa-eye" />
            <Button @click.prevent.stop="onSlideAction(slide, slide.action)" size="small" severity="secondary" class="mr-1" icon="fa fa-map-marker" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasScrollbar" class="map-data-carousel-nav-arrow right shadow-2" @click="navigateTo('right')"><i class="fa fa-chevron-right"/></div>
  </div>
</template>

<script>
import {defineComponent} from 'vue'

export default defineComponent({
  name: "MapCarouselControl",
  emits: ['reachLeftEdge', 'reachRightEdge', 'slideClick', 'slideAction'],
  props: {
    data: {
      type: Array,
      required: true
    },
    highlight: {
      type: Object,
      default: null
    },
    slideKey: {
      type: String,
      default: 'geometryId'
    },
    navAbsolute: {
      type: Boolean,
      default: false
    },
    orientation: {
      type: String,
      default: 'horizontal'
    }
  },
  data() {
    return {
      scrollAmount: 500,
      hasScrollbar: false
    }
  },
  updated(){
    this.checkScrollbar();
  },
  mounted() {
    this.checkScrollbar();

    if (this.highlight) {
      this.scrollIntoView(`slide-${this.highlight[this.slideKey]}`)
    }

    this.$watch('highlight', () => {
      if (this.highlight) {
        this.scrollIntoView(`slide-${this.highlight[this.slideKey]}`)
      }
    });
  },

  methods: {
    checkScrollbar() {
      this.hasScrollbar = this.$refs.slidesWrapper.scrollWidth > this.$refs.slidesWrapper.clientWidth
    },
    navigateTo(direction) {
      if (direction === 'right') {
        this.$refs.slidesWrapper.scrollLeft += this.scrollAmount;
      } else {
        this.$refs.slidesWrapper.scrollLeft -= this.scrollAmount;
      }
    },
    scroll() {
      if ((this.$refs.slidesWrapper.scrollWidth - this.$refs.slidesWrapper.scrollLeft) < this.scrollAmount * 4) {
        this.$emit('reachRightEdge');
        console.log('reachRightEdge');
      } else if (this.$refs.slidesWrapper.scrollLeft < this.scrollAmount) {
        this.$emit('reachLeftEdge');
        console.log('reachLeftEdge');
      }
    },
    scrollIntoView(ref) {
      if (!this.$refs[ref]) return;

      const element = this.$refs[ref][0];

      // Scroll the element into view horizontally
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start"
      });

      // Adjust the scroll position to make it align to the left side of the container
      this.$refs.slidesWrapper.scrollLeft += element.getBoundingClientRect().left - (this.$refs.slidesWrapper.clientWidth / 2) + 150; // last one is half of card width

      this.resetHighlights();
      element.classList.add('highlighted');
    },
    onSlideClick(slide) {
      this.resetHighlights();
      this.$emit('slideClick', slide);
      console.log('slideClick', slide);
    },
    onSlideAction(slide, action){
      this.resetHighlights();
      this.$emit('slideAction', slide, action);
      console.log('slideAction', slide, action);
    },
    resetHighlights() {
      document.querySelectorAll('.carousel-card').forEach(el => {
        el.classList.remove('highlighted');
      })
    },
    debounce(method, delay) {
      clearTimeout(method._tId);
      method._tId= setTimeout(()=>{
        method();
      }, delay);
    }
  }
})
</script>

<style scoped lang="scss">
:global(:root) {
  --gis-carousel-card-width: 310px;
  --gis-carousel-card-bg: white;
  --gis-carousel-img-width: 100px;
  --gis-carousel-radius: 5px;
  --gis-carousel-nav-bg: purple;
  --gis-carousel-nav-color: white;
  --gis-carousel-nav-size: 40px;
}

// temporary change
:global(.mapboxgl-ctrl-bottom){
  width: 85%;
}

.map-data-carousel-control {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .map-data-carousel-nav-arrow {
    background: var(--gis-carousel-nav-bg);
    color: var(--gis-carousel-nav-color);
    flex: 0 0 var(--gis-carousel-nav-size);
    width: var(--gis-carousel-nav-size);
    height: var(--gis-carousel-nav-size);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .map-data-carousel-slides {
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-behavior: smooth;

    .carousel-card {
      flex: 0 0 var(--gis-carousel-card-width);
      cursor: pointer;
      border-radius: var(--gis-carousel-radius);
      background: var(--gis-carousel-card-bg);
      display: flex;
      border: 2px solid transparent;
      transition: all 0.5s ease;

      > img {
        flex: 0 0 var(--gis-carousel-img-width);
        width: var(--gis-carousel-img-width);
        aspect-ratio: 1/1;
        border-top-left-radius: var(--gis-carousel-radius);
        border-bottom-left-radius: var(--gis-carousel-radius);
      }

      .carousel-card-text {
        padding: 10px;
      }

      &.highlighted,
      &:focus,
      &:hover {
        border: 2px solid #4141ff;
      }
    }
  }

  &.nav-absolute{
    .map-data-carousel-nav-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);

      &.left {
        left: 0;
      }

      &.right {
        right: 0;
      }
    }
  }

  &.vertical{

  }
}
</style>
